import React from 'react';
import {useTranslation} from 'react-i18next';
import {ArrowsClockwise, BellSlash, CheckCircle, Robot} from '../../icons';
import {
    alertItemFacts,
    alertItemTitle,
    elapsedSince,
    formatDate,
    severityOf,
} from '../../agency/v2/AlertsPopover';
import {alertLinesStillText, alertLinesText, alertPartialSnoozeText} from './alertLines';
import {AlertMailLine} from '../../alerts/AlertRecipients';
import {isRecalculating} from './recalculation';

// La línea de la fila se recalcula por un cambio guardado (card Fnvibexi): lo dice, para que el cambio no parezca
// ignorado. La bandeja se refresca sola mientras tanto (`useRecalculationRefresh`).
function AlertRecalculating({item}) {
    const {t} = useTranslation();
    if (!isRecalculating(item)) return null;
    return (
        <div className="cav-row__recalc" data-testid="cav-row-recalculating" role="status">
            <ArrowsClockwise size={12} weight="bold" className="cav-row__recalc-icon" aria-hidden="true"/>
            {t('line_recalculation.recalculating')}
        </div>
    );
}

/**
 * Las filas de una bandeja de alertas vigentes: la vigente (con Silenciar y Copiar prompt) y la
 * silenciada (con Reactivar). Salieron de `ClientAlertsView` (card j61xJLod) para «Ahora» del
 * Centro de Alertas (F3): el mismo ítem del endpoint `active-alerts`, el mismo dibujo.
 */
function AlertMeta({item, onReactivate, isSaving}) {
    const {t, i18n} = useTranslation();
    const parts = [];
    // Una alerta de varias líneas que miran lo mismo es UNA fila que las nombra (DP7i8uTf).
    const lines = alertLinesText(item, t, i18n.language);
    if (lines) parts.push(lines);
    parts.push(t('clients_v2.alert_meta_started', {elapsed: elapsedSince(item.first_seen, t)}));
    const partial = alertPartialSnoozeText(item, t, i18n.language);
    return (
        <div className="cav-row__meta">
            {parts.join(' · ')}
            {/* Que un problema vuelva es información, no ruido: se destaca. */}
            {item.occurrences > 1 && (
                <span className="cav-row__rep">
                    {` · ${t('clients_v2.alert_meta_fired', {count: item.occurrences})}`}
                </span>
            )}
            {/* Una línea silenciada de un grupo que sigue sonando (desde el Tablero, o un reactivar que
                falló en ella) se reactiva acá: la fila vigente no tiene otro lugar donde hacerlo. */}
            {partial && ` · ${partial}`}
            {partial && onReactivate && (
                <>
                    {' · '}
                    <button type="button" className="cav-row__link" disabled={isSaving}
                            onClick={() => onReactivate(item)}>
                        {t('clients_v2.alert_reactivate')}
                    </button>
                </>
            )}
        </div>
    );
}

function AlertFacts({item}) {
    const {t} = useTranslation();
    const facts = alertItemFacts(item, t);
    if (!facts.length) return null;
    return (
        <div className="cav-row__facts">
            {facts.map((fact, index) => (
                <React.Fragment key={fact}>
                    {index > 0 && ' · '}
                    <span className="cav-row__fact">{fact}</span>
                </React.Fragment>
            ))}
        </div>
    );
}

/**
 * `snoozeGuideAnchor`: el resultado de `anchorAttr(...)` para el «Silenciar» de ESTA fila (una sola).
 * `mailClientId`: el cliente de la alerta, para el renglón «Avisada por mail a N personas» (lo pasa «Ahora»
 * del Centro de Alertas; sin él, la fila es la de siempre).
 */
export function ActiveAlertRow({item, onSnooze, onCopyPrompt, onReactivate, isSaving, snoozeGuideAnchor = {},
                                mailClientId = null}) {
    const {t} = useTranslation();
    const severity = severityOf(item);
    return (
        <div className="cav-row">
            <span className={`cav-row__dot${severity ? ` -${severity}` : ''}`}/>
            <div className="cav-row__body">
                {severity && (
                    <div className={`cav-row__level -${severity}`}>
                        {t(`clients_v2.alert_level_${severity}`)}
                    </div>
                )}
                <div className="cav-row__title">{alertItemTitle(item, t)}</div>
                <AlertFacts item={item}/>
                <AlertMeta item={item} onReactivate={onReactivate} isSaving={isSaving}/>
                <AlertRecalculating item={item}/>
                {mailClientId != null && <AlertMailLine clientId={mailClientId} item={item}/>}
            </div>
            <div className="cav-row__acts">
                {item.snooze && item.snooze.available && (
                    <button type="button" className="cav-btn" onClick={() => onSnooze(item)} {...snoozeGuideAnchor}>
                        <BellSlash size={13} weight="bold"/>
                        {t('clients_v2.alert_snooze')}
                    </button>
                )}
                {item.prompt && (
                    <button type="button" className="cav-btn" onClick={() => onCopyPrompt(item)}>
                        <Robot size={13} weight="bold"/>
                        {t('clients_v2.alert_copy_prompt')}
                    </button>
                )}
            </div>
        </div>
    );
}

/**
 * La fila que se resolvió mientras se la miraba (/ux 04/11): queda en su lugar, en gris y sin acciones, hasta
 * salir de la pantalla. Sin esto se iba, y entre otras alertas no se notaba que el cambio había funcionado.
 * `phrase` es la de la recuperación, del backend y en el idioma de quien mira.
 */
export function ResolvedAlertRow({item, phrase}) {
    const {t, i18n} = useTranslation();
    const lines = alertLinesText(item, t, i18n.language);
    return (
        <div className="cav-row -resolved" data-testid="cav-row-resolved">
            <CheckCircle size={14} weight="bold" className="cav-row__resolved-icon" aria-hidden="true"/>
            <div className="cav-row__body">
                {/* Texto de React, no HTML: el escape de i18next sólo pondría entidades a la vista. */}
                <div className="cav-row__title">
                    {t('alerts.row_resolved', {phrase, interpolation: {escapeValue: false}})}
                </div>
                {lines && <div className="cav-row__meta">{lines}</div>}
            </div>
        </div>
    );
}

export function SnoozedAlertRow({item, onReactivate, isSaving}) {
    const {t, i18n} = useTranslation();
    const until = formatDate(item.snoozed_until, i18n.language);
    return (
        <div className="cav-row -snoozed">
            <span className="cav-row__dot"/>
            <div className="cav-row__body">
                <div className="cav-row__title">{alertItemTitle(item, t)}</div>
                <div className="cav-row__meta">
                    {alertLinesStillText(item, t, i18n.language)}
                </div>
                <span className="cav-row__silenced">
                    <BellSlash size={12} weight="bold"/>
                    {until
                        ? t('clients_v2.alert_snoozed_until', {date: until})
                        : t('clients_v2.alert_snoozed_forever')}
                </span>
            </div>
            <div className="cav-row__acts">
                {item.snooze && item.snooze.available && (
                    <button type="button" className="cav-btn" disabled={isSaving}
                            onClick={() => onReactivate(item)}>
                        {t('clients_v2.alert_reactivate')}
                    </button>
                )}
            </div>
        </div>
    );
}
